import { useRef, useState } from "react";
import { z } from "zod";
import { useAuth } from "@workos-inc/authkit-react";
import { toast } from "sonner";
import { createWorkspaceInviteRequest } from "@ano/shared/api/invites";
import { SharedApiError } from "@ano/shared/api/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SectionLabel } from "@/components/ui/section-label";
import { API_URL } from "@/lib/config";
import { getAccessTokenWithRetry } from "@/lib/auth-token";

const emailSchema = z.string().email();

interface QuickInviteProps {
  /**
   * The active workspace to invite the person into. The sidebar only mounts
   * QuickInvite in a workspace scope (never Home), so this is always set.
   */
  workspaceId: string;
}

export function QuickInvite({ workspaceId }: QuickInviteProps) {
  const { getAccessToken } = useAuth();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  // Re-entry guard: Enter on the input can fire `submit` before React has
  // re-rendered the disabled button, so the state-based `disabled` check
  // races. The ref reads synchronously.
  const inFlight = useRef(false);

  const normalized = email.trim().toLowerCase();
  const isValid = emailSchema.safeParse(normalized).success;
  const disabled = !isValid || sending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (inFlight.current || !isValid) return;
    inFlight.current = true;
    setSending(true);
    try {
      const token = await getAccessTokenWithRetry(getAccessToken, {
        maxWaitMs: 5000,
        intervalMs: 200,
      });
      if (!token) {
        toast.error("Authentication token unavailable");
        return;
      }
      await createWorkspaceInviteRequest({
        apiBaseUrl: API_URL,
        workspaceId,
        invitedEmail: normalized,
        accessToken: token,
      });
      toast.success(`Invite sent to ${normalized}`);
      setEmail("");
    } catch (err) {
      if (err instanceof SharedApiError && err.status === 429) {
        toast.error("Too many invites — try again in a minute");
      } else if (err instanceof Error) {
        toast.error(err.message);
      } else {
        toast.error("Failed to send invite");
      }
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  }

  return (
    <div className="px-2 pt-4 pb-4 border-t border-border/40">
      <SectionLabel variant="sidebar" className="mb-2.5 block">
        Quick invite
      </SectionLabel>
      <form
        onSubmit={handleSubmit}
        aria-label="Quick invite"
        className="space-y-2"
      >
        <Input
          type="email"
          inputMode="email"
          inputSize="tiny"
          autoComplete="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder="Invite by email…"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={sending}
          aria-label="Invite by email"
          className="border-border/60 bg-background shadow"
        />
        <Button
          type="submit"
          size="tiny"
          variant="outline"
          className="w-full"
          disabled={disabled}
        >
          {sending ? "Sending…" : "Send invite"}
        </Button>
      </form>
    </div>
  );
}
